// File upload service using backend API
// This service uploads files through the backend API which handles Cloudflare R2 integration

import appConfig from '../config/appConfig';
import apiService from './apiService';
import API_CONFIG from '../config/api';

class FileUploadService {
  constructor() {
    this.config = appConfig.cloudflare.r2;
  }

  /**
   * Upload file using backend API
   * @param {File} file - File to upload
   * @param {string} category - File category (e.g., 'marksheet', 'document')
   * @param {string} studentId - Student ID for folder structure
   * @param {string} customFileName - Custom filename (optional)
   * @returns {Promise<{success: boolean, url?: string, error?: string}>}
   */
  async uploadFile(file, category = 'document', studentId = '', customFileName = '') {
    try {
      // Validate file
      this.validateFile(file);

      // Create FormData for the API request
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('sub_directory', `students/${studentId}`);
      formData.append('max_size_mb', '5');

      // Check authentication token
      const token = sessionStorage.getItem('_auth_token_');
      console.log('Authentication token:', token ? `${token.substring(0, 10)}...` : 'No token found');

      console.log('Uploading file via backend API:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        category: category,
        subDirectory: `students/${studentId}`,
        hasToken: !!token
      });

      // Debug FormData contents
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      // Call backend API
      console.log('Making API request to /api/upload/single with:', {
        method: 'POST',
        hasFormData: !!formData,
        apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY ? `${API_CONFIG.KEYS.UNIVERSITY_KEY.substring(0, 10)}...` : 'No API key',
        authToken: token ? `${token.substring(0, 10)}...` : 'No auth token'
      });

      let response;
      try {
        response = await apiService.request('/api/upload/single', {
          method: 'POST',
          data: formData,
          apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY 
        });
      } catch (error) {
        console.error('Primary upload endpoint failed:', error);
        
        // Try alternative endpoints if the primary one fails
        console.log('Trying alternative upload endpoints...');
        
        // Try with different category if it's not marksheet
        if (category !== 'marksheet') {
          console.log(`Trying with category 'marksheet' instead of '${category}'`);
          const altFormData = new FormData();
          altFormData.append('file', file);
          altFormData.append('category', 'marksheet'); // Try marksheet category
          altFormData.append('sub_directory', `students/${studentId}`);
          altFormData.append('max_size_mb', '5');
          
          try {
            response = await apiService.request('/api/upload/single', {
              method: 'POST',
              data: altFormData,
              apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY 
            });
            console.log('Success with marksheet category fallback');
          } catch (marksheetError) {
            console.error('Marksheet category fallback also failed:', marksheetError);
          }
        }
        
        // If still no success, try alternative endpoint
        if (!response || !response.success) {
          try {
            response = await apiService.request('/api/upload', {
              method: 'POST',
              data: formData,
              apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY 
            });
          } catch (altError) {
            console.error('Alternative upload endpoint also failed:', altError);
            throw error; // Throw the original error
          }
        }
      }

      console.log('API Response:', {
        success: response.success,
        status: response.status,
        data: response.data,
        headers: response.headers
      });

      if (response.success) {
        return {
          success: true,
          url: response.data.data.file_url,
          fileName: response.data.data.file_name,
          filePath: response.data.data.file_path,
          originalName: response.data.data.original_name,
          fileSize: response.data.data.file_size,
          mimeType: response.data.data.mime_type
        };
      } else {
        console.error('Upload failed:', response);
        
        // Handle specific error cases
        if (response.status === 302) {
          throw new Error('Authentication redirect detected. Please check your login status and try again.');
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        } else if (response.status === 403) {
          throw new Error('Access denied. Please check your permissions.');
        } else if (response.status === 413) {
          throw new Error('File too large. Please reduce file size and try again.');
        } else {
          throw new Error(response.message || `Upload failed with status: ${response.status}`);
        }
      }
    } catch (error) {
      console.error('File upload error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Validate file before upload
   * @param {File} file - File to validate
   */
  validateFile(file) {
    if (!file) {
      throw new Error('No file provided');
    }

    if (file.size > appConfig.form.maxFileSize) {
      throw new Error(`File size exceeds maximum allowed size of ${appConfig.form.maxFileSize / (1024 * 1024)}MB`);
    }

    if (!appConfig.form.allowedFileTypes.includes(file.type)) {
      throw new Error(`File type ${file.type} is not allowed`);
    }
  }

  /**
   * Get public URL for a file path
   * @param {string} filePath - File path
   * @returns {string} Public URL
   */
  getPublicUrl(filePath) {
    // The backend API returns the full public URL, so we can return it directly
    return filePath;
  }
}

export default new FileUploadService();